import React, { useState, useEffect } from 'react';
import { Row, Col } from 'reactstrap';
import SyntaxHighlighter from 'react-syntax-highlighter';
import { atomOneDark } from 'react-syntax-highlighter/dist/esm/styles/hljs';
import './Documentation.scss';

const Documentation = () => {
  const [activeSection, setActiveSection] = useState('introduction');
  // Single language state for all selectors
  const [activeLanguage, setActiveLanguage] = useState('javascript');
  
  // Handle smooth scrolling to sections when clicking sidebar links
  const scrollToSection = (sectionId) => {
    setActiveSection(sectionId);
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      // Update URL with hash for sharing
      window.history.pushState(null, null, `#${sectionId}`);
    }
  };

  // Update active section based on scroll position
  useEffect(() => {
    const handleScroll = () => {
      const sections = document.querySelectorAll('.doc-section, .doc-subsection');
      let currentActive = 'introduction';
      
      sections.forEach(section => {
        const sectionTop = section.getBoundingClientRect().top;
        if (sectionTop < 150) {
          currentActive = section.id;
        }
      });
      
      setActiveSection(currentActive);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Set active section from URL hash on initial load
  useEffect(() => {
    const hash = window.location.hash.replace('#', '');
    if (hash) {
      setActiveSection(hash);
      const element = document.getElementById(hash);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, []);

  // Anchor link component for section headings
  const AnchorLink = ({ id, children, headingLevel }) => {
    const Tag = `h${headingLevel}`;
    
    return (
      <Tag id={id}>
        {children}
        <a 
          href={`#${id}`} 
          className="anchor-link"
          onClick={(e) => {
            e.preventDefault();
            scrollToSection(id);
          }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path>
            <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path>
          </svg>
        </a>
      </Tag>
    );
  };

  // Language selector component for reuse
  const LanguageSelector = ({ classes = '' }) => (
    <div className={`language-selector ${classes}`}>
      <div 
        className={`language-option ${activeLanguage === 'javascript' ? 'active' : ''}`}
        onClick={() => setActiveLanguage('javascript')}
      >
        JavaScript
      </div>
      <div 
        className={`language-option ${activeLanguage === 'ruby' ? 'active' : ''}`}
        onClick={() => setActiveLanguage('ruby')}
      >
        Ruby
      </div>
      <div 
        className={`language-option ${activeLanguage === 'csharp' ? 'active' : ''}`}
        onClick={() => setActiveLanguage('csharp')}
      >
        C#
      </div>
      <div 
        className={`language-option ${activeLanguage === 'php' ? 'active' : ''}`}
        onClick={() => setActiveLanguage('php')}
      >
        PHP
      </div>
      <div 
        className={`language-option ${activeLanguage === 'python' ? 'active' : ''}`}
        onClick={() => setActiveLanguage('python')}
      >
        Python
      </div>
      <div 
        className={`language-option ${activeLanguage === 'typescript' ? 'active' : ''}`}
        onClick={() => setActiveLanguage('typescript')}
      >
        TypeScript
      </div>
    </div>
  );

  const jsCode = `
const site = "https://example.com";
const url = \`https://opengraph.io/api/1.1/site/\${encodeURIComponent(site)}?app_id=xxxxxx\`;

async function fetchData() {
  try {
    const response = await fetch(url);
    const data = await response.json();
    console.log(data);
  } catch (error) {
    console.error("Fetch error:", error);
  }
}

fetchData();
`;

  const rubyCode = `require 'net/http'
require 'json'
require 'uri'

site = "https://example.com"
encoded_site = URI.encode_www_form_component(site)
url = URI("https://opengraph.io/api/1.1/site/#{encoded_site}?app_id=xxxxxx")

http = Net::HTTP.new(url.host, url.port)
http.use_ssl = (url.scheme == "https")

request = Net::HTTP::Get.new(url)
response = http.request(request)

data = JSON.parse(response.body)

puts data
`;

  const csharpCode = `using System;
using System.Net.Http;
using System.Threading.Tasks;
using Newtonsoft.Json;

class Program
{
    static async Task Main(string[] args)
    {
        string site = "https://example.com";
        string encodedSite = Uri.EscapeDataString(site);
        string appId = "xxxxxx";
        string url = $"https://opengraph.io/api/1.1/site/{encodedSite}?app_id={appId}";

        using (HttpClient httpClient = new HttpClient())
        {
            try
            {
                HttpResponseMessage response = await httpClient.GetAsync(url);
                response.EnsureSuccessStatusCode();

                string apiResponse = await response.Content.ReadAsStringAsync();
                dynamic data = JsonConvert.DeserializeObject(apiResponse);

                Console.WriteLine(data);
            }
            catch (Exception ex)
            {
                Console.WriteLine($"Request failed: {ex.Message}");
            }
        }
    }
}
`;

  const phpCode = `<?php
$site = 'https://example.com';
$encodedSite = urlencode($site);
$appId = 'xxxxxx';

$url = "https://opengraph.io/api/1.1/site/{$encodedSite}?app_id={$appId}";

$curl = curl_init($url);

curl_setopt($curl, CURLOPT_RETURNTRANSFER, true);
$response = curl_exec($curl);

if ($response === false) {
    die('Curl error: ' . curl_error($curl));
}

curl_close($curl);

$data = json_decode($response, true);

if (json_last_error() !== JSON_ERROR_NONE) {
    die('JSON decode error: ' . json_last_error_msg());
}

print_r($data);`;

  const pythonCode = `import requests
import json
import urllib.parse

site = "https://example.com"
encoded_site = urllib.parse.quote(site, safe='')  # Safe='' ensures full encoding
app_id = "xxxxxx"

url = f"https://opengraph.io/api/1.1/site/{encoded_site}?app_id={app_id}"

try:
    response = requests.get(url)
    response.raise_for_status()
    data = response.json()
    print(json.dumps(data, indent=2))
except requests.exceptions.RequestException as e:
    print(f"Error fetching data: {e}")
`;

  const tsCode = `// Define the interface for the API response structure
interface OpenGraphResponse {
  hybridGraph?: {
    title?: string;
    description?: string;
    type?: string;
    image?: string;
    url?: string;
    favicon?: string;
    site_name?: string;
    articlePublishedTime?: string;
    articleAuthor?: string;
  };
  openGraph?: {
    title?: string;
    description?: string;
    type?: string;
    image?: { url?: string };
    url?: string;
    site_name?: string;
    articlePublishedTime?: string;
    articleAuthor?: string;
  };
  htmlInferred?: {
    title?: string;
    description?: string;
    type?: string;
    image?: string;
    url?: string;
    favicon?: string;
    site_name?: string;
    images?: string[];
  };
  requestInfo?: {
    redirects?: number;
    host?: string;
    responseCode?: number;
    cache_ok?: boolean;
    max_cache_age?: number;
    accept_lang?: string;
    url?: string;
    full_render?: boolean;
    use_proxy?: boolean;
    use_superior?: boolean;
    responseContentType?: string;
  };
  accept_lang?: string;
  is_cache?: boolean;
  url?: string;
}

const site = "https://example.com";
const encodedSite = encodeURIComponent(site);
const appId = "xxxxxx";

const url = \`https://opengraph.io/api/1.1/site/\${encodedSite}?app_id=\${appId}\`;

async function fetchData(): Promise<void> {
  try {
    const response = await fetch(url);
    const data: OpenGraphResponse = await response.json();
    console.log(data);
  } catch (error) {
    console.error("Error fetching OpenGraph data:", error);
  }
}

fetchData();
`;

  const responseJson = `{
  "hybridGraph": {
    "title": "Example Title",
    "description": "Example Description",
    "type": "Example Type",
    "image": "https://example.com/image.png",
    "url": "https://example.com",
    "favicon": "https://example.com/favicon.ico",
    "site_name": "Example Site Name",
    "articlePublishedTime": "2023-03-23T00:00:00.000Z",
    "articleAuthor": "https://example.com/author"
  },
  "openGraph": {
    "title": "Example Title",
    "description": "Example Description",
    "type": "Example Type",
    "image": {
      "url": "https://example.com/image.png"
    },
    "url": "https://example.com",
    "site_name": "Example Site Name",
    "articlePublishedTime": "2023-03-23T00:00:00.000Z",
    "articleAuthor": "https://example.com/author"
  },
  "htmlInferred": {
    "title": "Example Title",
    "description": "Example Description",
    "type": "Example Type",
    "image": "https://example.com/image.png",
    "url": "https://example.com",
    "favicon": "https://example.com/favicon.ico",
    "site_name": "Example Site Name",
    "images": [
      "https://example.com/image1.png",
      "https://example.com/image2.png",
      "https://example.com/image3.png",
      "https://example.com/image4.png"
    ]
  },
  "requestInfo": {
    "redirects": 1,
    "host": "https://example.com",
    "responseCode": 200,
    "cache_ok": true,
    "max_cache_age": 432000000,
    "accept_lang": "en-US,en;q=0.9",
    "url": "https://example.com",
    "full_render": false,
    "use_proxy": false,
    "use_superior" : false,
    "responseContentType": "text/html; charset=utf-8"
  },
  "accept_lang": "en-US,en;q=0.9",
  "is_cache": false,
  "url": "https://example.com"
}`;

  const extractSiteJS = `const url =
  "https://opengraph.io/api/1.1/extract/:site?app_id=xxxxxx&html_elements=title,h1,h2,h3,h4,p";

const fetchData = async () => {
  try {
    const response = await fetch(url);
    const data = await response.json();
    console.log(data);
  } catch (error) {
    console.log(error);
  }
};

fetchData();`;

  const extractSiteRuby = `require 'net/http'
require 'json'

url = URI.parse("https://opengraph.io/api/1.1/extract/:site?app_id=xxxxxx&html_elements=title,h1,h2,h3,h4,p")
response = Net::HTTP.get_response(url)
data = JSON.parse(response.body)

puts data`;

  const extractSitePython = `import requests
import json

# Note: Remember to URL encode the :site parameter
url = "https://opengraph.io/api/1.1/extract/:site?app_id=xxxxxx&html_elements=title,h1,h2,h3,h4,p" 

try:
    response = requests.get(url)
    response.raise_for_status() 
    data = response.json()
    print(json.dumps(data, indent=2))
except requests.exceptions.RequestException as e:
    print(f"Error fetching data: {e}")`;

  const extractSiteCsharp = `using System;
using System.Net.Http;
using Newtonsoft.Json;

class Program
{
    static async System.Threading.Tasks.Task Main(string[] args)
    {
        var url = "https://opengraph.io/api/1.1/extract/:site?app_id=xxxxxx&html_elements=title,h1,h2,h3,h4,p";

        var httpClient = new HttpClient();
        var response = await httpClient.GetAsync(url);

        var jsonResponse = await response.Content.ReadAsStringAsync();
        dynamic data = JsonConvert.DeserializeObject(jsonResponse);

        Console.WriteLine(data);
    }
}`;

  const extractSitePhp = `$url = 'https://opengraph.io/api/1.1/extract/:site?app_id=xxxxxx&html_elements=title,h1,h2,h3,h4,p';
$ch = curl_init();

curl_setopt($ch, CURLOPT_URL, $url);
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);

$result = curl_exec($ch);

curl_close($ch);

$data = json_decode($result, true);

print_r($data);`;

  const extractSiteTS = `const url =
  "https://opengraph.io/api/1.1/extract/:site?app_id=xxxxxx&html_elements=title,h1,h2,h3,h4,p";

const fetchData = async (): Promise<void> => {
  try {
    const response = await fetch(url);
    const data: any = await response.json(); // Add type annotation if response structure is known
    console.log(data);
  } catch (error) {
    console.log(error);
  }
};

fetchData();`;

  const scrapeSiteJS = `const url = "https://opengraph.io/api/1.1/scrape/:site?app_id=xxxxxx";

const fetchData = async () => {
  try {
    const response = await fetch(url);
    const data = await response.json();
    console.log(data);
  } catch (error) {
    console.log(error);
  }
};

fetchData();`;

  const scrapeSiteRuby = `require 'net/http'
require 'json'

url = URI.parse("https://opengraph.io/api/1.1/scrape/:site?app_id=xxxxxx")
response = Net::HTTP.get_response(url)
data = JSON.parse(response.body)

puts data`;

  const scrapeSitePython = `import requests

# Note: Remember to URL encode the :site parameter
url = "https://opengraph.io/api/1.1/scrape/:site?app_id=xxxxxx"

try:
    response = requests.get(url)
    response.raise_for_status() 
    # Response is likely HTML content
    print(response.text) 
except requests.exceptions.RequestException as e:
    print(f"Error fetching data: {e}")`;

  const scrapeSiteCsharp = `using System;
using System.Net.Http;
using Newtonsoft.Json;

class Program
{
    static async System.Threading.Tasks.Task Main(string[] args)
    {
        var url = "https://opengraph.io/api/1.1/scrape/:site?app_id=xxxxxx";

        var httpClient = new HttpClient();
        var response = await httpClient.GetAsync(url);

        var jsonResponse = await response.Content.ReadAsStringAsync();
        dynamic data = JsonConvert.DeserializeObject(jsonResponse);

        Console.WriteLine(data);
    }
}`;

  const scrapeSitePhp = `$url = 'https://opengraph.io/api/1.1/scrape/:site?app_id=xxxxxx';
$ch = curl_init();

curl_setopt($ch, CURLOPT_URL, $url);
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);

$result = curl_exec($ch);

curl_close($ch);

$data = json_decode($result, true);

print_r($data);`;

  const scrapeSiteTS = `const url = "https://opengraph.io/api/1.1/scrape/:site?app_id=xxxxxx";

const fetchData = async (): Promise<void> => {
  try {
    const response = await fetch(url);
    // Response is likely HTML content, handle as text
    const data: string = await response.text(); 
    console.log(data);
  } catch (error) {
    console.log(error);
  }
};

fetchData();`;

  const screenshotSiteJS = `const url = "https://opengraph.io/api/1.1/screenshot/:site?app_id=xxxxxx";

const fetchData = async () => {
  try {
    const response = await fetch(url);
    const data = await response.json();
    console.log(data);
  } catch (error) {
    console.log(error);
  }
};

fetchData();`;

  const screenshotSiteRuby = `require 'net/http'
require 'open-uri'

url = URI.parse("https://opengraph.io/api/1.1/screenshot/:site?app_id=xxxxxx")

# Save the screenshot to a file
File.open("screenshot.png", "wb") do |file|
  file.write open(url).read
end

puts "Screenshot saved to screenshot.png"`;

  const screenshotSiteCsharp = `using System;
using System.Net.Http;
using System.IO;
using System.Threading.Tasks;

class Program
{
    static async Task Main(string[] args)
    {
        var url = "https://opengraph.io/api/1.1/screenshot/:site?app_id=xxxxxx";

        using (var httpClient = new HttpClient())
        {
            using (var response = await httpClient.GetAsync(url))
            {
                if (response.IsSuccessStatusCode)
                {
                    // Save the binary data to a file
                    byte[] imageBytes = await response.Content.ReadAsByteArrayAsync();
                    File.WriteAllBytes("screenshot.png", imageBytes);
                    
                    Console.WriteLine("Screenshot saved to screenshot.png");
                }
            }
        }
    }
}`;

  const screenshotSitePython = `import requests
import json

# Note: Remember to URL encode the :site parameter
url = "https://opengraph.io/api/1.1/screenshot/:site?app_id=xxxxxx"

try:
    response = requests.get(url)
    response.raise_for_status() 
    data = response.json() 
    # The response contains a URL to the screenshot
    print(json.dumps(data, indent=2)) 
except requests.exceptions.RequestException as e:
    print(f"Error fetching data: {e}")`;

  const screenshotSitePhp = `$url = 'https://opengraph.io/api/1.1/screenshot/:site?app_id=xxxxxx';
$ch = curl_init();

curl_setopt($ch, CURLOPT_URL, $url);
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);

$result = curl_exec($ch);

curl_close($ch);

// Save the binary data to a file
file_put_contents('screenshot.png', $result);

echo "Screenshot saved to screenshot.png";`;

  const screenshotSiteTS = `const url = "https://opengraph.io/api/1.1/screenshot/:site?app_id=xxxxxx";

const fetchData = async (): Promise<void> => {
  try {
    const response = await fetch(url);
    // The response contains a URL to the screenshot
    const data: { message: string; screenshotUrl: string } = await response.json(); 
    console.log(data);
  } catch (error) {
    console.log(error);
  }
};

fetchData();`;

  const scrapeSiteResponseJson = `The above command returns the Raw HTML of a Web Page:

<html>
  ...
</html>`;

  const screenshotResponseJson = `{
  "message": "Screenshot retrieved successfully",
  "screenshotUrl": "Url where the screenshot is stored"
}`;

  const embedSiteJS = `const url = "https://opengraph.io/api/1.1/oembed/:site?app_id=xxxxxx";

const fetchData = async () => {
  try {
    const response = await fetch(url);
    const data = await response.json();
    console.log(data);
  } catch (error) {
    console.log(error);
  }
};

fetchData();`;

  const embedSiteTS = `const url = "https://opengraph.io/api/1.1/oembed/:site?app_id=xxxxxx";

// Updated interface for the oEmbed response
interface EmbedResponse {
  type: string;
  version: string;
  provider_name?: string;
  width?: number;
  height?: number;
  html?: string;
}

const fetchData = async (): Promise<void> => {
  try {
    const response = await fetch(url);
    const data: EmbedResponse = await response.json(); 
    console.log(data);
  } catch (error) {
    console.log(error);
  }
};

fetchData();`;

  const embedSiteRuby = `require 'net/http'
require 'json'

url = URI.parse("https://opengraph.io/api/1.1/oembed/:site?app_id=xxxxxx")
response = Net::HTTP.get_response(url)
data = JSON.parse(response.body)

puts data`;

  const embedSitePython = `import requests
import json

# Note: Remember to URL encode the :site parameter
url = "https://opengraph.io/api/1.1/oembed/:site?app_id=xxxxxx" 

try:
    response = requests.get(url)
    response.raise_for_status() 
    data = response.json()
    print(json.dumps(data, indent=2))
except requests.exceptions.RequestException as e:
    print(f"Error fetching data: {e}")`;

  const embedSiteCsharp = `using System;
using System.Net.Http;
using Newtonsoft.Json;

class Program
{
    static async System.Threading.Tasks.Task Main(string[] args)
    {
        var url = "https://opengraph.io/api/1.1/oembed/:site?app_id=xxxxxx";

        var httpClient = new HttpClient();
        var response = await httpClient.GetAsync(url);

        var jsonResponse = await response.Content.ReadAsStringAsync();
        dynamic data = JsonConvert.DeserializeObject(jsonResponse);

        Console.WriteLine(data);
    }
}`;

  const embedSitePhp = `$url = 'https://opengraph.io/api/1.1/oembed/:site?app_id=xxxxxx';
$ch = curl_init();

curl_setopt($ch, CURLOPT_URL, $url);
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);

$result = curl_exec($ch);

curl_close($ch);

$data = json_decode($result, true);

print_r($data);`;

  const embedResponseJson = `{
    "type": "hybridEmbed",
    "version": "1.0",
    "provider_name": "Site Name",
    "width": 800,
    "height": 600,
    "html": "<iframe src=\"..."></iframe>"
}`;

  const getCodeExample = (language) => {
    switch (language) {
      case 'javascript':
        return jsCode;
      case 'ruby':
        return rubyCode;
      case 'csharp':
        return csharpCode;
      case 'php':
        return phpCode;
      case 'python':
        return pythonCode;
      case 'typescript':
        return tsCode;
      default:
        return jsCode;
    }
  };

  const getExtractSiteCodeExample = (language) => {
    switch (language) {
      case 'javascript':
        return extractSiteJS;
      case 'ruby':
        return extractSiteRuby;
      case 'csharp':
        return extractSiteCsharp;
      case 'php':
        return extractSitePhp;
      case 'python':
        return extractSitePython;
      case 'typescript':
        return extractSiteTS;
      default:
        return extractSiteJS;
    }
  };
  
  const getScrapeSiteCodeExample = (language) => {
    switch (language) {
      case 'javascript':
        return scrapeSiteJS;
      case 'ruby':
        return scrapeSiteRuby;
      case 'csharp':
        return scrapeSiteCsharp;
      case 'php':
        return scrapeSitePhp;
      case 'python':
        return scrapeSitePython;
      case 'typescript':
        return scrapeSiteTS;
      default:
        return scrapeSiteJS;
    }
  };

  const getScreenshotSiteCodeExample = (language) => {
    switch (language) {
      case 'javascript':
        return screenshotSiteJS;
      case 'ruby':
        return screenshotSiteRuby;
      case 'csharp':
        return screenshotSiteCsharp;
      case 'php':
        return screenshotSitePhp;
      case 'python':
        return screenshotSitePython;
      case 'typescript':
        return screenshotSiteTS;
      default:
        return screenshotSiteJS;
    }
  };

  const getEmbedSiteCodeExample = (language) => {
    switch (language) {
      case 'javascript':
        return embedSiteJS;
      case 'typescript':
        return embedSiteTS;
        case 'ruby':
        return embedSiteRuby;
      case 'csharp':
        return embedSiteCsharp;
      case 'php':
        return embedSitePhp;
      case 'python':
        return embedSitePython;
      default:
        return embedSiteJS;
    }
  };

  // Add language-specific code examples for the Query Site endpoint
  const getQuerySiteCodeExample = (language) => {
    switch (language) {
      case 'php':
        return `<?php
      $site = 'https://example.com';
      $query = 'What is the main headline?';
      $responseStructure = '{"headline": "string"}';

      $encodedSite = urlencode($site);
      $encodedQuery = urlencode($query);
      $encodedResponseStructure = urlencode($responseStructure);

      $url = "https://opengraph.io/api/1.1/query/{$encodedSite}?app_id=xxxxxx&query={$encodedQuery}&responseStructure={$encodedResponseStructure}";

      $ch = curl_init();
      curl_setopt($ch, CURLOPT_URL, $url);
      curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);
      $response = curl_exec($ch);
      curl_close($ch);

      $data = json_decode($response, true);
      print_r($data);
      ?>`;
      case 'python':
        return `import requests
import urllib.parse

site = "https://example.com"
query = "What is the main headline?"
response_structure = '{"headline": "string"}'

url = f"https://opengraph.io/api/1.1/query/{urllib.parse.quote(site)}?app_id=xxxxxx&query={urllib.parse.quote(query)}&responseStructure={urllib.parse.quote(response_structure)}"

response = requests.get(url)
data = response.json()
print(data)
`;
      case 'ruby':
        return `require 'net/http'
require 'uri'
require 'json'

site = "https://example.com"
query = "What is the main headline?"
response_structure = '{"headline": "string"}'

encoded_site = URI.encode_www_form_component(site)
encoded_query = URI.encode_www_form_component(query)
encoded_response_structure = URI.encode_www_form_component(response_structure)

url = URI("https://opengraph.io/api/1.1/query/#{encoded_site}?app_id=xxxxxx&query=#{encoded_query}&responseStructure=#{encoded_response_structure}")

response = Net::HTTP.get(url)
data = JSON.parse(response)
puts data
`;
      case 'csharp':
        return `using System;
using System.Net.Http;
using System.Threading.Tasks;
using System.Web;

class Program
{
    static async Task Main(string[] args)
    {
        string site = "https://example.com";
        string query = "What is the main headline?";
        string responseStructure = "{\"headline\": \"string\"}";
        string url = $"https://opengraph.io/api/1.1/query/{Uri.EscapeDataString(site)}?app_id=xxxxxx&query={Uri.EscapeDataString(query)}&responseStructure={Uri.EscapeDataString(responseStructure)}";

        using (HttpClient client = new HttpClient())
        {
            var response = await client.GetAsync(url);
            string result = await response.Content.ReadAsStringAsync();
            Console.WriteLine(result);
        }
    }
}
`;
      case 'java':
        return `import java.io.BufferedReader;
import java.io.InputStreamReader;
import java.net.HttpURLConnection;
import java.net.URLEncoder;
import java.net.URL;

public class Main {
    public static void main(String[] args) throws Exception {
        String site = "https://example.com";
        String query = "What is the main headline?";
        String responseStructure = "{\"headline\": \"string\"}";
        String urlStr = "https://opengraph.io/api/1.1/query/" +
            URLEncoder.encode(site, "UTF-8") +
            "?app_id=xxxxxx&query=" + URLEncoder.encode(query, "UTF-8") +
            "&responseStructure=" + URLEncoder.encode(responseStructure, "UTF-8");
        URL url = new URL(urlStr);
        HttpURLConnection conn = (HttpURLConnection) url.openConnection();
        conn.setRequestMethod("GET");
        BufferedReader in = new BufferedReader(new InputStreamReader(conn.getInputStream()));
        String inputLine;
        StringBuilder content = new StringBuilder();
        while ((inputLine = in.readLine()) != null) {
            content.append(inputLine);
        }
        in.close();
        conn.disconnect();
        System.out.println(content.toString());
    }
}
`;
      case 'javascript':
      default:
        return `const site = "https://example.com";
const query = "What is the main headline?";
const responseStructure = '{"headline": "string"}';
const url = "https://opengraph.io/api/1.1/query/" + encodeURIComponent(site) + "?app_id=xxxxxx&query=" + encodeURIComponent(query) + "&responseStructure=" + encodeURIComponent(responseStructure);

async function fetchData() {
  const response = await fetch(url);
  const data = await response.json();
  console.log(data);
}
fetchData();
`;
    }
  };

  // Sidebar component for documentation sections
  const DocumentationSidebar = () => (
    <div className="documentation-sidebar">
      <div className="documentation-sidebar__title">Contents</div>
      <ul className="documentation-sidebar__list">
        <li className="documentation-sidebar__item">
          <a 
            href="#introduction" 
            className={activeSection === 'introduction' ? 'active' : ''}
            onClick={(e) => { e.preventDefault(); scrollToSection('introduction'); }}
          >
            Introduction
          </a>
        </li>
        <li className="documentation-sidebar__item">
          <a 
            href="#authentication" 
            className={activeSection === 'authentication' ? 'active' : ''}
            onClick={(e) => { e.preventDefault(); scrollToSection('authentication'); }}
          >
            Authentication
          </a>
        </li>
        <li className="documentation-sidebar__item">
          <a 
            href="#api-endpoints" 
            className={activeSection === 'api-endpoints' ? 'active' : ''}
            onClick={(e) => { e.preventDefault(); scrollToSection('api-endpoints'); }}
          >
            API Endpoints
          </a>
          <ul className="documentation-sidebar__list">
            <li className="documentation-sidebar__item documentation-sidebar__subitem">
              <a 
                href="#get-open-graph" 
                className={activeSection === 'get-open-graph' ? 'active' : ''}
                onClick={(e) => { e.preventDefault(); scrollToSection('get-open-graph'); }}
              >
                Get Open Graph
              </a>
            </li>
            <li className="documentation-sidebar__item documentation-sidebar__subitem">
              <a 
                href="#extract-site" 
                className={activeSection === 'extract-site' ? 'active' : ''}
                onClick={(e) => { e.preventDefault(); scrollToSection('extract-site'); }}
              >
                Extract Site
              </a>
            </li>
            <li className="documentation-sidebar__item documentation-sidebar__subitem">
              <a 
                href="#scrape-site" 
                className={activeSection === 'scrape-site' ? 'active' : ''}
                onClick={(e) => { e.preventDefault(); scrollToSection('scrape-site'); }}
              >
                Scrape Site
              </a>
            </li>
            <li className="documentation-sidebar__item documentation-sidebar__subitem">
              <a 
                href="#screenshot-site" 
                className={activeSection === 'screenshot-site' ? 'active' : ''}
                onClick={(e) => { e.preventDefault(); scrollToSection('screenshot-site'); }}
              >
                Screenshot Site
              </a>
            </li>
            <li className="documentation-sidebar__item documentation-sidebar__subitem">
              <a 
                href="#embed-site" 
                className={activeSection === 'embed-site' ? 'active' : ''}
                onClick={(e) => { e.preventDefault(); scrollToSection('embed-site'); }}
              >
                Embed Site
              </a>
            </li>
            <li className="documentation-sidebar__item documentation-sidebar__subitem">
              <a 
                href="#query-site" 
                className={activeSection === 'query-site' ? 'active' : ''}
                onClick={(e) => { e.preventDefault(); scrollToSection('query-site'); }}
              >
                Query Site
              </a>
            </li>
          </ul>
        </li>
        <li className="documentation-sidebar__item">
          <a 
            href="#requests" 
            className={activeSection === 'requests' ? 'active' : ''}
            onClick={(e) => { e.preventDefault(); scrollToSection('requests'); }}
          >
            API Requests
          </a>
          <ul className="documentation-sidebar__list">
            <li className="documentation-sidebar__item documentation-sidebar__subitem">
              <a 
                href="#how-requests-work" 
                className={activeSection === 'how-requests-work' ? 'active' : ''}
                onClick={(e) => { e.preventDefault(); scrollToSection('how-requests-work'); }}
              >
                How Requests Work
              </a>
            </li>
            <li className="documentation-sidebar__item documentation-sidebar__subitem">
              <a 
                href="#auto-proxy" 
                className={activeSection === 'auto-proxy' ? 'active' : ''}
                onClick={(e) => { e.preventDefault(); scrollToSection('auto-proxy'); }}
              >
                Auto Proxy
              </a>
            </li>
          </ul>
        </li>
        <li className="documentation-sidebar__item">
          <a 
            href="#mcp-integration" 
            className={activeSection === 'mcp-integration' ? 'active' : ''}
            onClick={(e) => { e.preventDefault(); scrollToSection('mcp-integration'); }}
          >
            MCP Integration
          </a>
          <ul className="documentation-sidebar__list">
            <li className="documentation-sidebar__item documentation-sidebar__subitem">
              <a 
                href="#mcp-overview" 
                className={activeSection === 'mcp-overview' ? 'active' : ''}
                onClick={(e) => { e.preventDefault(); scrollToSection('mcp-overview'); }}
              >
                Overview
              </a>
            </li>
            <li className="documentation-sidebar__item documentation-sidebar__subitem">
              <a 
                href="#mcp-setup" 
                className={activeSection === 'mcp-setup' ? 'active' : ''}
                onClick={(e) => { e.preventDefault(); scrollToSection('mcp-setup'); }}
              >
                Setup & Running
              </a>
            </li>
            <li className="documentation-sidebar__item documentation-sidebar__subitem">
              <a 
                href="#mcp-integration-cursor" 
                className={activeSection === 'mcp-integration-cursor' ? 'active' : ''}
                onClick={(e) => { e.preventDefault(); scrollToSection('mcp-integration-cursor'); }}
              >
                Cursor Integration
              </a>
            </li>
            <li className="documentation-sidebar__item documentation-sidebar__subitem">
              <a 
                href="#mcp-troubleshooting" 
                className={activeSection === 'mcp-troubleshooting' ? 'active' : ''}
                onClick={(e) => { e.preventDefault(); scrollToSection('mcp-troubleshooting'); }}
              >
                Troubleshooting
              </a>
            </li>
          </ul>
        </li>
      </ul>
    </div>
  );

  return (
    <div className="container">
      <div className="documentation-container">
        <DocumentationSidebar />

        <div className="documentation-content-wrapper">
          <div className="documentation-header">
            <h1>OpenGraph.io API Documentation</h1>
            <p>Welcome to the API documentation for Opengraph.io! With our API, you can easily parse HTML from URLs provided for Open Graph tags.</p>
          </div>

          <div className="global-language-selector">
            <p>Set language for all examples:</p>
            <LanguageSelector />
          </div>

          <div className="documentation-content">
            <section id="introduction" className="doc-section">
              <AnchorLink id="introduction" headingLevel={2}>Introduction</AnchorLink>
              <p>
                Welcome to the API documentation for Opengraph.io! With our API, you can easily parse HTML from URLs provided for Open Graph tags.
              </p>
              <p>
                We have language bindings in NodeJS, Ruby, PHP, C#, and JQuery! You can view code examples in the dark area to the right, and you can switch the programming language of the examples with the tabs above each example.
              </p>
            </section>

            <section id="authentication" className="doc-section">
              <AnchorLink id="authentication" headingLevel={2}>Authentication</AnchorLink>
              <div className="code-block">
                <code>https://opengraph.io/api/1.1/site/&lt;URL encoded site URL&gt;?app_id=xxxxxx</code>
              </div>
              <p>
                To authenticate your API requests, simply include your unique app_id query parameter with each request. This app_id serves as your API key and is provided to you upon registration.
              </p>
              <p>
                To get started with the Opengraph.io API, you'll need to create a free account to receive your app_id. <a href="/register">Opengraph.io Signup</a>
              </p>
              <div className="info-box">
                <p>You must replace <code>xxxxxx</code> with your personal API key.</p>
              </div>
            </section>

            <section id="api-endpoints" className="doc-section">
              <AnchorLink id="api-endpoints" headingLevel={2}>API Endpoints</AnchorLink>
              
              <section id="get-open-graph" className="doc-subsection">
                <AnchorLink id="get-open-graph" headingLevel={3}>Get Open Graph</AnchorLink>
                <div className="code-examples">
                  <div className="code-example">
                    <div className="code-example-header">
                      <h4>REQUEST</h4>
                      <LanguageSelector classes="compact-selector" />
                    </div>
                    <SyntaxHighlighter language={activeLanguage} style={atomOneDark}>
                      {getCodeExample(activeLanguage)}
                    </SyntaxHighlighter>
                  </div>
                  <div className="response-example">
                    <h4>RESPONSE</h4>
                    <SyntaxHighlighter language="json" style={atomOneDark}>
                      {responseJson}
                    </SyntaxHighlighter>
                  </div>
                </div>

                <h4>HTTP Request</h4>
                <div className="code-block">
                  <code>GET https://opengraph.io/api/1.1/site/&lt;URL encoded site URL&gt;?app_id=xxxxxx</code>
                </div>

                <h4>URL Parameters</h4>
                <div className="params-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Parameter</th>
                        <th>Required</th>
                        <th>Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>:site</td>
                        <td>true</td>
                        <td>This is a required parameter and is an encoded URL of the website from which you want open graph information.</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <h4>Query Parameters</h4>
                <div className="params-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Parameter</th>
                        <th>Required</th>
                        <th>Example</th>
                        <th>Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>app_id</td>
                        <td>yes</td>
                        <td>-</td>
                        <td>The API key for registered users. Create an account (no cc ever required) to receive your app_id.</td>
                      </tr>
                      <tr>
                        <td>cache_ok</td>
                        <td>no</td>
                        <td>false</td>
                        <td>This will force our servers to pull a fresh version of the site being requested. By default this value is true</td>
                      </tr>
                      <tr>
                        <td>full_render</td>
                        <td>no</td>
                        <td>false</td>
                        <td>This will fully render the site using a chrome browser before parsing its contents. This is especially helpful for single page applications and JS redirects. This will slow down the time it takes to get a response by around 1.5 seconds.</td>
                      </tr>
                      <tr>
                        <td>use_proxy</td>
                        <td>no</td>
                        <td>false</td>
                        <td>Route your request through residential and mobile proxies to avoid bot detection. This will slow down requests 3-10 seconds and can cause requests to time out. NOTE: Proxies are a limited resource and expensive for our team maintain. Free accounts share a small pool of proxies. If you plan on using proxies often, paid accounts provide dedicated concurrent proxies for your account.</td>
                      </tr>
                      <tr>
                        <td>use_premium</td>
                        <td>no</td>
                        <td>false</td>
                        <td>The Premium Proxy feature in our API allows you to leverage residential and mobile proxy pools for enhanced scraping performance.</td>
                      </tr>
                      <tr>
                        <td>use_superior</td>
                        <td>no</td>
                        <td>false</td>
                        <td>The Superior Proxy feature is designed to tackle the most demanding scraping scenarios, allowing you to overcome the challenges posed by highly restrictive websites. By leveraging our superior proxy option, you can bypass bot detection mechanisms and access data from even the toughest sources.</td>
                      </tr>
                      <tr>
                        <td>auto_proxy</td>
                        <td>no</td>
                        <td>false</td>
                        <td>By default, the Auto Proxy is turned on and will use a proxy for any domain that our team has identified as requiring one to bypass bot detection. Set the auto_proxy query parameter to false if you would like to turn the feature off. If you find a domain that needs a proxy but isn't being automatically used to fetch the data, please contact support, and we will update our Auto Proxy settings accordingly.</td>
                      </tr>
                      <tr>
                        <td>max_cache_age</td>
                        <td>no</td>
                        <td>432000000</td>
                        <td>This specifies the maximum age in milliseconds that a cached response should be. If not specified the value is set to 5 days. (5 days * 24 hours * 60 minutes * 60 seconds * 1000ms = 432,000,000 ms)</td>
                      </tr>
                      <tr>
                        <td>accept_lang</td>
                        <td>no</td>
                        <td>en-US,en;q=0.9 auto</td>
                        <td>This specifies the request language sent when requesting the url. This is useful if you want to get the site for languages other than english. The default setting for this will return an english version of a page if it exists. Note: if you specify the value auto the api will use the same language settings of your current request. For more information on what to supply for this field please see: <a href="https://developer.mozilla.org/en-US/docs/Web/HTTP/Headers/Accept-Language" target="_blank" rel="noopener noreferrer">https://developer.mozilla.org/en-US/docs/Web/HTTP/Headers/Accept-Language</a></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div className="info-box">
                  <p>Remember — <code>app_id</code> is required, and is your API KEY used to talk to the Opengraph.io API.</p>
                </div>
              </section>

              <section id="extract-site" className="doc-subsection">
                <AnchorLink id="extract-site" headingLevel={3}>Extract Site</AnchorLink>
                <p>
                  The extract endpoint enables you to extract information from any website by providing its URL. With this endpoint, you can extract any element you need from the website, including but not limited to the title, header elements (h1 to h5), and paragraph elements (p).
                </p>
                <div className="code-examples">
                  <div className="code-example">
                    <div className="code-example-header">
                      <h4>REQUEST</h4>
                      <LanguageSelector classes="compact-selector" />
                    </div>
                    <SyntaxHighlighter language={activeLanguage} style={atomOneDark}>
                      {getExtractSiteCodeExample(activeLanguage)}
                    </SyntaxHighlighter>
                  </div>
                  <div className="response-example">
                    <h4>RESPONSE</h4>
                    <SyntaxHighlighter language="json" style={atomOneDark}>
                      {`{
  "tags": [
    {
      "tag": "title",
      "innerText": "example innerText",
      "position": 0
    },
    {
      "tag": "h1",
      "innerText": "example innerText",
      "position": 1
    },
    {
      "tag": "h2",
      "innerText": "example innerText",
      "position": 2
    },
    {
      "tag": "h2",
      "innerText": "example innerText",
      "position": 3
    },
    {
      "tag": "h2",
      "innerText": "example innerText",
      "position": 4
    },
    {
      "tag": "h3",
      "innerText": "example innerText",
      "position": 5
    },
    {
      "tag": "h3",
      "innerText": "example innerText",
      "position": 6
    },
    {
      "tag": "h4",
      "innerText": "example innerText",
      "position": 7
    },
    {
      "tag": "p",
      "innerText": "example innerText",
      "position": 8
    }
  ],
  "concatenatedText": "The concatenatedText property represents the combined text content of all the tags associated with the object, merged into a single string. This property provides a convenient way to access and manipulate the entire text content of the object at once, rather than having to iterate through each tag individually."
}`}
                    </SyntaxHighlighter>
                  </div>
                </div>

                <h4>HTTP Request</h4>
                <div className="code-block">
                  <code>GET /api/1.1/extract/:site?app_id=xxxxxx</code>
                </div>

                <h4>URL Parameters</h4>
                <div className="params-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Parameter</th>
                        <th>Required</th>
                        <th>Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>:site</td>
                        <td>true</td>
                        <td>This is a required parameter and is an encoded URL of the website from which you want open graph information.</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <h4>Query Parameters</h4>
                <div className="params-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Parameter</th>
                        <th>Required</th>
                        <th>Example</th>
                        <th>Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>app_id</td>
                        <td>yes</td>
                        <td>-</td>
                        <td>The API key for registered users. Create an account (no cc ever required) to receive your app_id.</td>
                      </tr>
                      <tr>
                        <td>html_elements</td>
                        <td>no</td>
                        <td>h1,h2,h3,p,span</td>
                        <td>This is an optional parameter and specifies the HTML elements you want to extract from the website. The value should be a comma-separated list of HTML element names. If this parameter is not supplied, the default elements that will be extracted are h1, h2, h3, h4, h5, p, and title.</td>
                      </tr>
                      <tr>
                        <td>cache_ok</td>
                        <td>no</td>
                        <td>false</td>
                        <td>This will force our servers to pull a fresh version of the site being requested. By default this value is true</td>
                      </tr>
                      <tr>
                        <td>full_render</td>
                        <td>no</td>
                        <td>false</td>
                        <td>This will fully render the site using a chrome browser before parsing its contents. This is especially helpful for single page applications and JS redirects. This will slow down the time it takes to get a response by around 1.5 seconds.</td>
                      </tr>
                      <tr>
                        <td>use_proxy</td>
                        <td>no</td>
                        <td>false</td>
                        <td>Route your request through residential and mobile proxies to avoid bot detection. This will slow down requests 3-10 seconds and can cause requests to time out. NOTE: Proxies are a limited resource and expensive for our team maintain. Free accounts share a small pool of proxies. If you plan on using proxies often, paid accounts provide dedicated concurrent proxies for your account.</td>
                      </tr>
                      <tr>
                        <td>use_premium</td>
                        <td>no</td>
                        <td>false</td>
                        <td>The Premium Proxy feature in our API allows you to leverage residential and mobile proxy pools for enhanced scraping performance.</td>
                      </tr>
                      <tr>
                        <td>use_superior</td>
                        <td>no</td>
                        <td>false</td>
                        <td>The Superior Proxy feature is designed to tackle the most demanding scraping scenarios, allowing you to overcome the challenges posed by highly restrictive websites. By leveraging our superior proxy option, you can bypass bot detection mechanisms and access data from even the toughest sources.</td>
                      </tr>
                      <tr>
                        <td>auto_proxy</td>
                        <td>no</td>
                        <td>false</td>
                        <td>By default, the Auto Proxy is turned on and will use a proxy for any domain that our team has identified as requiring one to bypass bot detection. Set the auto_proxy query parameter to false if you would like to turn the feature off. If you find a domain that needs a proxy but isn't being automatically used to fetch the data, please contact support, and we will update our Auto Proxy settings accordingly.</td>
                      </tr>
                      <tr>
                        <td>max_cache_age</td>
                        <td>no</td>
                        <td>432000000</td>
                        <td>This specifies the maximum age in milliseconds that a cached response should be. If not specified the value is set to 5 days. (5 days * 24 hours * 60 minutes * 60 seconds * 1000ms = 432,000,000 ms)</td>
                      </tr>
                      <tr>
                        <td>accept_lang</td>
                        <td>no</td>
                        <td>en-US,en;q=0.9 auto</td>
                        <td>This specifies the request language sent when requesting the url. This is useful if you want to get the site for languages other than english. The default setting for this will return an english version of a page if it exists. Note: if you specify the value auto the api will use the same language settings of your current request. For more information on what to supply for this field please see: <a href="https://developer.mozilla.org/en-US/docs/Web/HTTP/Headers/Accept-Language" target="_blank" rel="noopener noreferrer">https://developer.mozilla.org/en-US/docs/Web/HTTP/Headers/Accept-Language</a></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                
                <h4>Note</h4>
                <div className="info-box">
                  <p>The response will only include the elements specified in the html_elements query or the default elements if the query is not supplied.</p>
                  <p>If the website does not contain any of the specified elements, the corresponding keys in the response will be empty lists.</p>
                  <p>Remember — <code>app_id</code> is required, and is your API KEY used to talk to the Opengraph.io API.</p>
                </div>
              </section>

              <section id="scrape-site" className="doc-subsection">
                <AnchorLink id="scrape-site" headingLevel={3}>Scrape Site</AnchorLink>
                <p>
                  The scrape endpoint allows you to retrieve the full HTML content of any website. This is useful when you need to process or analyze the entire HTML structure of a page.
                </p>
                
                <div className="code-examples">
                  <div className="code-example">
                    <div className="code-example-header">
                      <h4>REQUEST</h4>
                      <LanguageSelector classes="compact-selector" />
                    </div>
                    <SyntaxHighlighter language={activeLanguage} style={atomOneDark}>
                      {getScrapeSiteCodeExample(activeLanguage)}
                    </SyntaxHighlighter>
                  </div>
                  <div className="response-example">
                    <h4>RESPONSE</h4>
                    <SyntaxHighlighter language="html" style={atomOneDark}>
                      {scrapeSiteResponseJson}
                    </SyntaxHighlighter>
                  </div>
                </div>

                <h4>HTTP Request</h4>
                <div className="code-block">
                  <code>GET https://opengraph.io/api/1.1/scrape/:site?app_id=xxxxxx</code>
                </div>

                <h4>URL Parameters</h4>
                <div className="params-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Parameter</th>
                        <th>Required</th>
                        <th>Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>:site</td>
                        <td>true</td>
                        <td>This is a required parameter and is an encoded URL of the website from which you want open graph information.</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <h4>Query Parameters</h4>
                <div className="params-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Parameter</th>
                        <th>Required</th>
                        <th>Example</th>
                        <th>Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>app_id</td>
                        <td>yes</td>
                        <td>-</td>
                        <td>The API key for registered users. Create an account (no cc ever required) to receive your app_id.</td>
                      </tr>
                      <tr>
                        <td>cache_ok</td>
                        <td>no</td>
                        <td>false</td>
                        <td>This will force our servers to pull a fresh version of the site being requested. By default this value is true</td>
                      </tr>
                      <tr>
                        <td>full_render</td>
                        <td>no</td>
                        <td>false</td>
                        <td>This will fully render the site using a chrome browser before parsing its contents. This is especially helpful for single page applications and JS redirects. This will slow down the time it takes to get a response by around 1.5 seconds.</td>
                      </tr>
                      <tr>
                        <td>use_proxy</td>
                        <td>no</td>
                        <td>false</td>
                        <td>Route your request through residential and mobile proxies to avoid bot detection. This will slow down requests 3-10 seconds and can cause requests to time out. NOTE: Proxies are a limited resource and expensive for our team maintain. Free accounts share a small pool of proxies. If you plan on using proxies often, paid accounts provide dedicated concurrent proxies for your account.</td>
                      </tr>
                      <tr>
                        <td>use_premium</td>
                        <td>no</td>
                        <td>false</td>
                        <td>The Premium Proxy feature in our API allows you to leverage residential and mobile proxy pools for enhanced scraping performance.</td>
                      </tr>
                      <tr>
                        <td>use_superior</td>
                        <td>no</td>
                        <td>false</td>
                        <td>The Superior Proxy feature is designed to tackle the most demanding scraping scenarios, allowing you to overcome the challenges posed by highly restrictive websites. By leveraging our superior proxy option, you can bypass bot detection mechanisms and access data from even the toughest sources.</td>
                      </tr>
                      <tr>
                        <td>auto_proxy</td>
                        <td>no</td>
                        <td>false</td>
                        <td>By default, the Auto Proxy is turned on and will use a proxy for any domain that our team has identified as requiring one to bypass bot detection. Set the auto_proxy query parameter to false if you would like to turn the feature off. If you find a domain that needs a proxy but isn't being automatically used to fetch the data, please contact support, and we will update our Auto Proxy settings accordingly.</td>
                      </tr>
                      <tr>
                        <td>max_cache_age</td>
                        <td>no</td>
                        <td>432000000</td>
                        <td>This specifies the maximum age in milliseconds that a cached response should be. If not specified the value is set to 5 days. (5 days * 24 hours * 60 minutes * 60 seconds * 1000ms = 432,000,000 ms)</td>
                      </tr>
                      <tr>
                        <td>accept_lang</td>
                        <td>no</td>
                        <td>en-US,en;q=0.9 auto</td>
                        <td>This specifies the request language sent when requesting the url. This is useful if you want to get the site for languages other than english. The default setting for this will return an english version of a page if it exists. Note: if you specify the value auto the api will use the same language settings of your current request. For more information on what to supply for this field please see: <a href="https://developer.mozilla.org/en-US/docs/Web/HTTP/Headers/Accept-Language" target="_blank" rel="noopener noreferrer">https://developer.mozilla.org/en-US/docs/Web/HTTP/Headers/Accept-Language</a></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                
                <div className="info-box">
                  <p>Remember — <code>app_id</code> is required, and is your API KEY used to talk to the Opengraph.io API.</p>
                </div>
              </section>

              <section id="screenshot-site" className="doc-subsection">
                <AnchorLink id="screenshot-site" headingLevel={3}>Screenshot Site</AnchorLink>
                <p>
                  The screenshot endpoint enables you to capture a full-page screenshot of any website. This is useful for visual verification, reporting, or creating previews of websites.
                </p>
                
                <div className="warning-box">
                  <p><strong>Important:</strong> Screenshot images must be downloaded within 24 hours. We automatically remove screenshots after this time period.</p>
                </div>
                
                <div className="code-examples">
                  <div className="code-example">
                    <div className="code-example-header">
                      <h4>REQUEST</h4>
                      <LanguageSelector classes="compact-selector" />
                    </div>
                    <SyntaxHighlighter language={activeLanguage} style={atomOneDark}>
                      {getScreenshotSiteCodeExample(activeLanguage)}
                    </SyntaxHighlighter>
                  </div>
                  <div className="response-example">
                    <h4>RESPONSE</h4>
                    <SyntaxHighlighter language="json" style={atomOneDark}>
                      {screenshotResponseJson}
                    </SyntaxHighlighter>
                  </div>
                </div>

                <h4>HTTP Request</h4>
                <div className="code-block">
                  <code>GET /api/1.1/screenshot/:site?app_id=xxxxxx</code>
                </div>

                <h4>URL Parameters</h4>
                <div className="params-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Parameter</th>
                        <th>Required</th>
                        <th>Default Value</th>
                        <th>Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>app_id</td>
                        <td>yes</td>
                        <td>-</td>
                        <td>The API key for registered users. Create an account (no cc ever required) to receive your app_id.</td>
                      </tr>
                      <tr>
                        <td>full_page</td>
                        <td>no</td>
                        <td>false</td>
                        <td>The full_page query parameter determines whether the screenshot should capture the visible viewport or the entire content of the page.</td>
                      </tr>
                      <tr>
                        <td>dimensions</td>
                        <td>no</td>
                        <td>md</td>
                        <td>The dimensions query parameter sets the viewport of the screen.</td>
                      </tr>
                      <tr>
                        <td>quality</td>
                        <td>no</td>
                        <td>80</td>
                        <td>The quality query parameter is used to specify the quality of the screenshot.</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                
                <h4>Full Page Parameter</h4>
                <p>
                  The <code>full_page</code> parameter determines whether the screenshot should capture the visible viewport or the entire content of the page. 
                  The parameter can be set to one of the following values:
                </p>
                <ul>
                  <li><strong>true:</strong> Capture the entire content of the page.</li>
                  <li><strong>false:</strong> Capture the visible viewport.</li>
                </ul>
                
                <h4>Dimensions Parameter</h4>
                <p>
                  The <code>dimensions</code> parameter specifies the viewport of the screen and can be set to one of the following values:
                </p>
                <ul>
                  <li>
                    <p><strong>lg:</strong></p>
                    <ul>
                      <li><code>width:</code> 1920</li>
                      <li><code>height:</code> 1080</li>
                    </ul>
                  </li>
                  <li>
                    <p><strong>md:</strong></p>
                    <ul>
                      <li><code>width:</code> 1366</li>
                      <li><code>height:</code> 768</li>
                    </ul>
                  </li>
                  <li>
                    <p><strong>sm:</strong></p>
                    <ul>
                      <li><code>width:</code> 1024</li>
                      <li><code>height:</code> 768</li>
                    </ul>
                  </li>
                  <li>
                    <p><strong>xs:</strong></p>
                    <ul>
                      <li><code>width:</code> 375</li>
                      <li><code>height:</code> 812</li>
                    </ul>
                  </li>
                </ul>
                
                <h4>Quality Parameter</h4>
                <p>
                  The <code>quality</code> parameter specifies the image quality is set to a value representing different quality levels. 
                  The value should be set in intervals of 10, starting from 10 up to 80, where:
                </p>
                <ul>
                  <li><code>10</code> is the lowest quality.</li>
                  <li><code>80</code> is the highest quality.</li>
                </ul>
                <p><strong>Valid values</strong>: 10, 20, 30, 40, 50, 60, 70, 80</p>
                
                <div className="info-box">
                  <p>Remember — <code>app_id</code> is required, and is your API KEY used to talk to the Opengraph.io API.</p>
                </div>
              </section>

              <section id="embed-site" className="doc-subsection">
                <AnchorLink id="embed-site" headingLevel={3}>Embed Site</AnchorLink>
                <p>
                  The oEmbed endpoint provides an easy way to get embeddable representations of URLs, following the oEmbed specification.
                </p>
                
                <div className="code-examples">
                  <div className="code-example">
                    <div className="code-example-header">
                      <h4>REQUEST</h4>
                      <LanguageSelector classes="compact-selector" />
                    </div>
                    <SyntaxHighlighter language={activeLanguage} style={atomOneDark}>
                      {getEmbedSiteCodeExample(activeLanguage)}
                    </SyntaxHighlighter>
                  </div>
                  <div className="response-example">
                    <h4>RESPONSE</h4>
                    <SyntaxHighlighter language="json" style={atomOneDark}>
                      {embedResponseJson}
                    </SyntaxHighlighter>
                  </div>
                </div>

                <h4>HTTP Request</h4>
                <div className="code-block">
                  <code>GET https://opengraph.io/api/1.1/oembed/:site?app_id=xxxxxx</code>
                </div>

                <h4>URL Parameters</h4>
                <div className="params-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Parameter</th>
                        <th>Required</th>
                        <th>Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>:site</td>
                        <td>true</td>
                        <td>The URL-encoded URL of the resource to embed.</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <h4>Query Parameters</h4>
                <div className="params-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Parameter</th>
                        <th>Required</th>
                        <th>Example</th>
                        <th>Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>app_id</td>
                        <td>yes</td>
                        <td>-</td>
                        <td>Your API key.</td>
                      </tr>
                      {/* Add other oEmbed specific parameters here if needed, e.g., maxwidth, maxheight, format */}
                    </tbody>
                  </table>
                </div>
                
                <div className="info-box">
                  <p>Remember — <code>app_id</code> is required, and is your API KEY used to talk to the Opengraph.io API.</p>
                </div>
              </section>
              <section id="query-site" className="doc-subsection">
                <AnchorLink id="query-site" headingLevel={3}>Query Site</AnchorLink>
                <p>
                  The <strong>Query</strong> endpoint allows you to send a custom query to a website and receive a structured response. You can optionally specify a <code>responseStructure</code> parameter to shape the response you want (for example, a JSON structure or specific fields).
                </p>
                <div className="code-examples">
                  <div className="code-example">
                    <div className="code-example-header">
                      <h4>REQUEST</h4>
                      <LanguageSelector classes="compact-selector" />
                    </div>
                    <SyntaxHighlighter language={activeLanguage} style={atomOneDark}>
                      {getQuerySiteCodeExample(activeLanguage)}
                    </SyntaxHighlighter>
                  </div>
                  <div className="response-example">
                    <h4>RESPONSE</h4>
                    <SyntaxHighlighter language="json" style={atomOneDark}>
                      {`{
  "headline": "Example Headline from the site"
}`}
                    </SyntaxHighlighter>
                  </div>
                </div>
                <h4>HTTP Request</h4>
                <div className="code-block">
                  <code>GET https://opengraph.io/api/1.1/query/&lt;URL encoded site URL&gt;?app_id=xxxxxx&amp;query=...&amp;responseStructure=...</code>
                </div>
                <h4>URL Parameters</h4>
                <div className="params-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Parameter</th>
                        <th>Required</th>
                        <th>Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>:site</td>
                        <td>true</td>
                        <td>The URL-encoded URL of the site you want to query.</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <h4>Query Parameters</h4>
                <div className="params-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Parameter</th>
                        <th>Required</th>
                        <th>Example</th>
                        <th>Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>app_id</td>
                        <td>yes</td>
                        <td>-</td>
                        <td>Your API key.</td>
                      </tr>
                      <tr>
                        <td>query</td>
                        <td>yes</td>
                        <td>What is the main headline?</td>
                        <td>The query you want to run against the site.</td>
                      </tr>
                      <tr>
                        <td>responseStructure</td>
                        <td>no</td>
                        <td><code>{'{"headline": "string"}'}</code></td>
                        <td>Optionally specify the structure of the response you want (e.g., a JSON schema or field list).</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div className="info-box">
                  <p><code>responseStructure</code> is optional. If provided, the API will attempt to shape the response to match your requested structure.</p>
                </div>
              </section>
            </section>

            <section id="requests" className="doc-section">
              <AnchorLink id="requests" headingLevel={2}>API Requests</AnchorLink>
              <p>Requests to the API can vary in cost. A cached response will only ever cost 1, while a request requiring javascript execution and a proxy could cost up to 20 requests. We outline the variations in cost below.</p>
              
              <section id="how-requests-work" className="doc-subsection">
                <AnchorLink id="how-requests-work" headingLevel={3}>How Requests Work</AnchorLink>
                <p>Each billing cycle allows for a specific number of requests. The number of available requests is determined by your active plan. How many requests used on each request can depend on certain query parameters.</p>
                
                <h4>Requests Used</h4>
                <div className="params-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Feature</th>
                        <th>Cost</th>
                        <th>Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>request</td>
                        <td>1</td>
                        <td>A request is a single API call to our system.</td>
                      </tr>
                      <tr>
                        <td>full_render</td>
                        <td>10</td>
                        <td>full_render is a request that will render the page in a headless browser and return the full HTML of the page.</td>
                      </tr>
                      <tr>
                        <td>use_proxy</td>
                        <td>10</td>
                        <td>use_proxy will route a request through one of our proxy servers. This is useful for scraping sites that have basic scraping protection.</td>
                      </tr>
                      <tr>
                        <td>use_premium</td>
                        <td>20</td>
                        <td>use_premium will route a request through one of our premium proxy servers.</td>
                      </tr>
                      <tr>
                        <td>screenshot</td>
                        <td>20</td>
                        <td>A call to the screenshot API route will be charged at 20 requests.</td>
                      </tr>
                      <tr>
                        <td>use_superior</td>
                        <td>30</td>
                        <td>use_superior tells opengraph.io to utilize a more advanced premium proxy which bypasses advanced scraping protection employed by sites such as LinkedIn, Amazon, etc.</td>
                      </tr>
                      <tr>
                        <td>Auto Proxy</td>
                        <td>varies</td>
                        <td>The Auto Proxy feature instructs the API to automatically use a proxy whenever necessary. This feature can be turned off by setting the auto_proxy query parameter to false. If you encounter sites that require a proxy but are not being automatically scraped with one, please contact support. If a request triggers the proxy, the request will be billed for the proxy that was required.</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </section>

              <section id="auto-proxy" className="doc-subsection">
                <AnchorLink id="auto-proxy" headingLevel={3}>Auto Proxy</AnchorLink>
                <p>To make things as smooth and simple as possible we have created the Auto Proxy feature to automatically use a proxy for sites with bot detection.</p>
                <p>The Auto Proxy feature is on by default for qualifying plans, but can be turned off by setting the <code>auto_proxy</code> query parameter to <code>false</code>.</p>
                <p>If you discover a site that needs a proxy that our Auto Proxy is not aware of, please feel free to contact support.</p>
              </section>
            </section>

            <section id="mcp-integration" className="doc-section">
              <h2>MCP Integration</h2>
              <p>
                OpenGraph.io offers Model-Context-Protocol (MCP) integration, allowing AI agents like Anthropic Claude, Cursor, and LangGraph to access our API capabilities through a standard interface without exposing raw API keys.
              </p>
              
              <section id="mcp-overview" className="doc-subsection">
                <h3>Overview</h3>
                <p>
                  The og-mcp server acts as a bridge between AI clients and the OpenGraph.io API:
                </p>
                <div className="info-box">
                  <pre>
{`┌─────────────┐          MCP (JSON‑RPC)           ┌──────────────┐
│  AI Client  │ ───────────────────────────────▶ │   og‑mcp     │
└─────────────┘                                  │  Server      │
        ▲                                        └──────┬───────┘
        │                                               │ REST
        │                                               ▼
        │                                    ┌────────────────────┐
        └─────────────────────────────────── │  api.opengraph.io  │
                                             └────────────────────┘`}
                  </pre>
                </div>
                <p>The flow works as follows:</p>
                <ol>
                  <li>AI client makes a tool call to one of the available MCP functions</li>
                  <li>og-mcp server receives the request and formats it for the OpenGraph.io API</li>
                  <li>OpenGraph.io processes the request and returns data</li>
                  <li>og-mcp transforms the response into a format suitable for the AI client</li>
                  <li>AI client receives the structured data ready for use</li>
                </ol>
                <p>
                  Available MCP tools include:
                </p>
                <div className="params-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Tool Name</th>
                        <th>OpenGraph.io API Endpoint</th>
                        <th>Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>Get OG Data</td>
                        <td>/api/1.1/site/&lt;URL&gt;</td>
                        <td>Extracts Open Graph data from a URL</td>
                      </tr>
                      <tr>
                        <td>Get OG Scrape Data</td>
                        <td>/api/1.1/scrape/&lt;URL&gt;</td>
                        <td>Scrapes data from a URL using OpenGraph's scrape endpoint</td>
                      </tr>
                      <tr>
                        <td>Get OG Screenshot</td>
                        <td>/api/1.1/screenshot/&lt;URL&gt;</td>
                        <td>Gets a screenshot of a webpage using OpenGraph's screenshot endpoint</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </section>

              <section id="mcp-setup" className="doc-subsection">
                <h3>Setup & Running</h3>
                <p>To set up the og-mcp server:</p>
                <ol>
                  <li>Install the package globally: <code>npm install -g opengraph-io-mcp</code></li>
                  <li>Or clone the repository and run:
                    <div className="code-block">
                      <SyntaxHighlighter language="bash" style={atomOneDark}>
                        {`npm install
npm run build
npm start`}
                      </SyntaxHighlighter>
                    </div>
                  </li>
                </ol>
                
                <h4>Configuration</h4>
                <p>The server requires an OpenGraph.io App ID, which you can provide in several ways:</p>
                <ul>
                  <li>Using environment variables: Set <code>OPENGRAPH_APP_ID</code> or <code>APP_ID</code> in a <code>.env</code> file or as an environment variable</li>
                  <li>Using command-line arguments with stdio transport: <code>--app-id YOUR_APP_ID</code></li>
                  <li>When using SSE transport: Include it in the URL as a query parameter (<code>?app_id=YOUR_APP_ID</code>)</li>
                </ul>
                
                <h4>Transport Options</h4>
                <p><strong>Stdio Transport (Recommended)</strong></p>
                <div className="code-block">
                  <SyntaxHighlighter language="bash" style={atomOneDark}>
                    {`npm run start:stdio
# Or with app id
npm run start:stdio -- --app-id YOUR_APP_ID
# When installed globally
opengraph-io-mcp --app-id YOUR_APP_ID`}
                  </SyntaxHighlighter>
                </div>
                
                <p><strong>HTTP/SSE Transport</strong></p>
                <div className="code-block">
                  <SyntaxHighlighter language="bash" style={atomOneDark}>
                    {'npm start'}
                  </SyntaxHighlighter>
                </div>
                <p>This runs a web server that can be accessed over HTTP and uses SSE for streaming.</p>
              </section>

              <section id="mcp-integration-cursor" className="doc-subsection">
                <h3>Cursor Integration</h3>
                <p>To integrate the og-mcp server with Cursor:</p>
                
                <h4>Using stdio transport (Recommended)</h4>
                <p>If you've installed the package globally, add this to your Cursor mcp.json:</p>
                <div className="code-block">
                  <SyntaxHighlighter language="json" style={atomOneDark}>
                    {`{
    "mcpServers": {
        "og-mcp": {
            "command": "opengraph-io-mcp",
            "env": {
                "APP_ID": "YOUR_APP_ID"
            }
        }
    }
}`}
                  </SyntaxHighlighter>
                </div>
                
                <p>Using npx:</p>
                <div className="code-block">
                  <SyntaxHighlighter language="json" style={atomOneDark}>
                    {`{
    "mcpServers": {
        "og-mcp": {
            "command": "npx",
            "args": ["opengraph-io-mcp"],
            "env": {
                "APP_ID": "YOUR_APP_ID"
            }
        }
    }
}`}
                  </SyntaxHighlighter>
                </div>
                
                <h4>Using HTTP/SSE Transport</h4>
                <p>Start the MCP server and add this to your Cursor mcp.json:</p>
                <div className="code-block">
                  <SyntaxHighlighter language="json" style={atomOneDark}>
                    {`{
    "mcpServers": {
        "og-mcp": {
            "url": "http://localhost:3010/sse?app_id=YOUR_APP_ID"
        }
    }
}`}
                  </SyntaxHighlighter>
                </div>

                <h4>Windsurf Integration</h4>
                <p>For Windsurf, add to your <code>./codeium/windsurf/model_config.json</code>:</p>
                <div className="code-block">
                  <SyntaxHighlighter language="json" style={atomOneDark}>
                    {`{
  "mcpServers": {
    "og-mcp": {
      "command": "opengraph-io-mcp",
      "env": {
        "APP_ID": "YOUR_APP_ID"
      }
    }
  }
}`}
                  </SyntaxHighlighter>
                </div>
              </section>

              <section id="mcp-troubleshooting" className="doc-subsection">
                <h3>Troubleshooting</h3>
                <ul>
                  <li>If tools aren't showing up, check that the server is running and the URL is correctly configured in Cursor</li>
                  <li>Check the server logs for any connection or authorization issues</li>
                  <li>Verify that Claude has been instructed to use the specific tools by name</li>
                </ul>
              </section>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Documentation; 